// Color constants matching PDFTron's legacy dark UI theme
const COLORS = {
  background: '#3a3a3a',
  backgroundHover: '#4a4a4a',
  border: '#4a4a4a',
  divider: '#2a2a2a',
  text: '#e0e0e0',
  scrollbar: '#5a5a5a'
} as const

export const SCALE_OPTIONS = [
  { value: 1, label: 'Default 1:1', type: 'imperial' as const },
  { value: 3.0, label: '3" = 1\'-0"', type: 'imperial' as const },
  { value: 1.5, label: '1 1/2" = 1\'-0"', type: 'imperial' as const },
  { value: 0.75, label: '3/4" = 1\'-0"', type: 'imperial' as const },
  { value: 0.5, label: '1/2" = 1\'-0"', type: 'imperial' as const },
  { value: 0.375, label: '3/8" = 1\'-0"', type: 'imperial' as const },
  { value: 0.25, label: '1/4" = 1\'-0"', type: 'imperial' as const },
  { value: 0.1875, label: '3/16" = 1\'-0"', type: 'imperial' as const },
  { value: 0.125, label: '1/8" = 1\'-0"', type: 'imperial' as const },
  { value: 0.09375, label: '3/32" = 1\'-0"', type: 'imperial' as const },
  { value: 0.0625, label: '1/16" = 1\'-0"', type: 'imperial' as const },
  { value: 0.03125, label: '1/32" = 1\'-0"', type: 'imperial' as const },
  { value: 0.015625, label: '1/64" = 1\'-0"', type: 'imperial' as const },
  { value: 10, label: '1:10', type: 'metric' as const },
  { value: 20, label: '1:20', type: 'metric' as const },
  { value: 50, label: '1:50', type: 'metric' as const },
  { value: 75, label: '1:75', type: 'metric' as const },
  { value: 100, label: '1:100', type: 'metric' as const },
  { value: 125, label: '1:125', type: 'metric' as const },
  { value: 200, label: '1:200', type: 'metric' as const },
  { value: 500, label: '1:500', type: 'metric' as const },
  { value: 1000, label: '1:1000', type: 'metric' as const },
  { value: 5000, label: '1:5000', type: 'metric' as const }
] as const

export type ScaleOption = (typeof SCALE_OPTIONS)[number]

/**
 * Creates a custom scale selector dropdown for injection into the PDFTron annotation popup.
 * Must be rendered inside a PDFTron iframe document.
 *
 * @param onScaleChange - Callback fired with the selected scale value
 * @param doc - The iframe document to create elements in (defaults to global document)
 * @param initialScale - The currently selected scale value; used to pre-select the matching option
 */
export function scalesDropdown(
  onScaleChange?: (scale: number) => void,
  doc: Document = document,
  initialScale?: number
): HTMLElement {
  const initialOption =
    initialScale != null
      ? (SCALE_OPTIONS.find((o) => o.value === initialScale) ?? SCALE_OPTIONS[0])
      : SCALE_OPTIONS[0]

  const container = doc.createElement('div')
  container.style.position = 'relative'
  container.style.display = 'inline-block'
  container.style.minWidth = '140px'
  container.style.marginTop = '2px'
  container.classList.add('mbue-scales-dropdown')

  const header = doc.createElement('div')
  header.style.display = 'flex'
  header.style.alignItems = 'center'
  header.style.justifyContent = 'space-between'
  header.style.padding = '8px 12px'
  header.style.backgroundColor = COLORS.background
  header.style.color = COLORS.text
  header.style.borderRadius = '6px'
  header.style.cursor = 'pointer'
  header.style.userSelect = 'none'
  header.style.fontSize = '14px'
  header.style.border = `1px solid ${COLORS.border}`

  const labelEl = doc.createElement('span')
  labelEl.textContent = initialOption.label
  labelEl.style.flex = '1'

  const chevron = doc.createElement('span')
  chevron.innerHTML =
    '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6,9 12,15 18,9"></polyline></svg>'
  chevron.style.display = 'inline-flex'
  chevron.style.color = COLORS.text

  header.appendChild(labelEl)
  header.appendChild(chevron)

  const panel = doc.createElement('div')
  panel.style.position = 'absolute'
  panel.style.top = '100%'
  panel.style.left = '0'
  panel.style.right = '0'
  panel.style.marginTop = '4px'
  panel.style.backgroundColor = COLORS.background
  panel.style.borderRadius = '6px'
  panel.style.border = `1px solid ${COLORS.border}`
  panel.style.boxShadow = '0 4px 12px rgba(0,0,0,0.4)'
  panel.style.maxHeight = '280px'
  panel.style.overflowY = 'auto'
  panel.style.zIndex = '9999'
  panel.style.display = 'none'
  panel.style.scrollbarWidth = 'thin'
  panel.style.scrollbarColor = `${COLORS.scrollbar} ${COLORS.background}`

  let outsideClickHandler: ((e: MouseEvent) => void) | null = null
  const removeOutsideClick = () => {
    if (outsideClickHandler) {
      doc.removeEventListener('click', outsideClickHandler)
      outsideClickHandler = null
    }
  }

  SCALE_OPTIONS.forEach((optionItem) => {
    const isSelected =
      optionItem.value === initialOption.value && optionItem.label === initialOption.label
    const option = doc.createElement('div')
    option.textContent = optionItem.label
    option.style.padding = '8px 12px'
    option.style.color = COLORS.text
    option.style.fontSize = '14px'
    option.style.cursor = 'pointer'
    option.style.borderBottom = `1px solid ${COLORS.divider}`
    option.style.backgroundColor = isSelected ? COLORS.backgroundHover : 'transparent'

    option.addEventListener('mouseenter', () => {
      option.style.backgroundColor = COLORS.backgroundHover
    })
    option.addEventListener('mouseleave', () => {
      option.style.backgroundColor = isSelected ? COLORS.backgroundHover : 'transparent'
    })
    option.addEventListener('click', () => {
      labelEl.textContent = optionItem.label
      onScaleChange?.(optionItem.value)
      panel.style.display = 'none'
      removeOutsideClick()
    })
    panel.appendChild(option)
  })

  header.addEventListener('click', (ev) => {
    ev.stopPropagation()
    const isOpen = panel.style.display === 'block'
    panel.style.display = isOpen ? 'none' : 'block'
    if (isOpen) {
      removeOutsideClick()
    } else {
      outsideClickHandler = (e: MouseEvent) => {
        if (!container.contains(e.target as Node)) {
          panel.style.display = 'none'
          removeOutsideClick()
        }
      }
      // Delay to avoid immediately re-closing on the same click event
      setTimeout(() => doc.addEventListener('click', outsideClickHandler!), 0)
    }
  })

  container.appendChild(header)
  container.appendChild(panel)
  return container
}
